import axios from 'axios';

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:42026';

class AuthService {
  private token: string | null = null;
  private user: any = null;
  private isLoggingOut: boolean = false;

  constructor() {
    // Load token from localStorage
    this.token = localStorage.getItem('privacypal_token');
    this.user = this.getStoredUser();

    axios.defaults.headers.common['x-pp-client-id'] = 'privacypal-user-portal';
    
    // Set up axios interceptor to handle token expiration
    this.setupAxiosInterceptor();
  }

  private setupAxiosInterceptor() {
    // Response interceptor to handle 401 errors (token expiration)
    axios.interceptors.response.use(
      (response) => response,
      async (error) => {
        // Check if it's a 401 error and not from a public endpoint
        if (error.response?.status === 401) {
          const url = error.config?.url || '';
          const isPublicEndpoint =
            url.includes('/api/user/login') ||
            url.includes('/api/user/register') ||
            url.includes('/api/user/signup/') ||
            url.includes('/api/user/verify-email') ||
            url.includes('/api/user/resend-verification') ||
            url.includes('/api/user/password-rules') ||
            url.includes('/api/user/forgot-password') ||
            url.includes('/api/user/reset-password') ||
            url.includes('/api/auth/sso/complete') ||
            url.includes('/api/user/validate-token');
          
          // Only auto-logout for authenticated endpoints
          if (!isPublicEndpoint && !this.isLoggingOut) {
            this.isLoggingOut = true;
            console.log('Session expired. Automatically signing out...');
            
            // Clear auth state
            await this.logout();
            
            // Redirect to login page
            // Use window.location to ensure a full page reload and clear any stale state
            if (window.location.pathname !== '/login') {
              window.location.href = '/login?session_expired=true';
            }
          }
        }
        
        return Promise.reject(error);
      }
    );
  }

  private getStoredUser() {
    const userStr = localStorage.getItem('privacypal_user');
    return userStr ? JSON.parse(userStr) : null;
  }

  async login(email: string, password: string) {
    try {
      const response = await axios.post(`${API_BASE_URL}/api/user/login`, {
        email,
        password
      });

      if (response.data?.data?.token || response.data?.token) {
        const token = response.data?.data?.token || response.data?.token;
        const user = response.data?.data?.user || response.data?.user || { email };

        this.token = token;
        this.user = user;

        localStorage.setItem('privacypal_token', token);
        localStorage.setItem('privacypal_user', JSON.stringify(user));

        // Set default auth header
        axios.defaults.headers.common['x-access-token'] = token;

        return { success: true, user: this.user };
      }

      return { success: false, error: 'Invalid credentials' };
    } catch (error: any) {
      const status = error.response?.status;
      const data = error.response?.data || {};
      // Surface the 403 EMAIL_NOT_VERIFIED case so the UI can render a
      // resend-verification prompt instead of a generic error.
      if (status === 403 && data.code === 'EMAIL_NOT_VERIFIED') {
        return {
          success: false,
          emailNotVerified: true,
          email: data.email || email,
          error: data.error || 'Please verify your email before signing in.'
        };
      }
      return {
        success: false,
        error: data.error || error.message || 'Login failed'
      };
    }
  }

  /**
   * Request a 6-digit signup verification code. The API always answers with
   * the same neutral message whether or not the address has an account.
   */
  async requestSignupCode(email: string) {
    try {
      const response = await axios.post(`${API_BASE_URL}/api/user/signup/start`, { email });
      return { success: true, message: response.data?.message };
    } catch (error: any) {
      const status = error.response?.status;
      if (status === 429) {
        return { success: false, error: 'Too many requests. Please wait a few minutes and try again.' };
      }
      return {
        success: false,
        error: error.response?.data?.error || error.message || 'Could not send a verification code'
      };
    }
  }

  /** Exchange the typed code for a short-lived single-use mailbox proof. */
  async verifySignupCode(email: string, code: string) {
    try {
      const response = await axios.post(`${API_BASE_URL}/api/user/signup/verify`, { email, code });
      if (response.data?.verified && response.data?.signupToken) {
        return { success: true, signupToken: response.data.signupToken as string };
      }
      return { success: false, error: response.data?.error || 'That code is invalid or has expired.' };
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.error || error.message || 'That code is invalid or has expired.'
      };
    }
  }

  /** Complete an SSO login that required mailbox proof (untrusted email claim). */
  async ssoComplete(pendingToken: string, code: string) {
    try {
      const response = await axios.post(`${API_BASE_URL}/api/auth/sso/complete`, { pendingToken, code });
      if (response.data?.success && response.data?.tokens?.accessToken) {
        const token = response.data.tokens.accessToken;
        const user = response.data.user;
        this.token = token;
        this.user = user;
        localStorage.setItem('privacypal_token', token);
        localStorage.setItem('privacypal_user', JSON.stringify(user));
        axios.defaults.headers.common['x-access-token'] = token;
        return { success: true, user };
      }
      return { success: false, error: response.data?.error || 'Verification failed' };
    } catch (error: any) {
      const data = error.response?.data || {};
      return {
        success: false,
        expiredSession: data.code === 'SSO_VERIFY_EXPIRED',
        error: data.error || error.message || 'Verification failed'
      };
    }
  }

  async register(firstName: string, lastName: string, email: string, password: string, signupToken?: string) {
    try {
      const response = await axios.post(`${API_BASE_URL}/api/user/register`, {
        firstName,
        lastName,
        email,
        password,
        signupToken
      });

      // Verify-first flow: register returns a session token immediately (the
      // account is born verified — the code was proven before this call).
      if (response.data?.token) {
        const token = response.data.token;
        const user = response.data.user || {
          id: response.data.newUserId,
          email: email,
          firstName: firstName,
          lastName: lastName
        };

        this.token = token;
        this.user = user;

        localStorage.setItem('privacypal_token', token);
        localStorage.setItem('privacypal_user', JSON.stringify(user));

        axios.defaults.headers.common['x-access-token'] = token;

        return { success: true, user: this.user };
      }

      return { success: false, error: response.data?.error || 'Registration failed' };
    } catch (error: any) {
      const errorMessage = error.response?.data?.error || error.message || 'Network error';
      return {
        success: false,
        error: errorMessage
      };
    }
  }

  async verifyEmail(token: string) {
    try {
      const response = await axios.post(`${API_BASE_URL}/api/user/verify-email`, { token });
      if (response.data?.token && response.data?.user) {
        const accessToken = response.data.token;
        const user = response.data.user;
        this.token = accessToken;
        this.user = user;
        localStorage.setItem('privacypal_token', accessToken);
        localStorage.setItem('privacypal_user', JSON.stringify(user));
        axios.defaults.headers.common['x-access-token'] = accessToken;
        return { success: true, user };
      }
      return { success: false, error: 'Verification response missing token' };
    } catch (error: any) {
      const data = error.response?.data || {};
      return {
        success: false,
        expired: data.code === 'VERIFICATION_EXPIRED',
        email: data.email,
        error: data.error || error.message || 'Verification failed'
      };
    }
  }

  async resendVerification(email: string) {
    try {
      const response = await axios.post(`${API_BASE_URL}/api/user/resend-verification`, { email });
      return { success: true, message: response.data?.message };
    } catch (error: any) {
      // The API returns a neutral 200 even on failure, so this should rarely fire.
      return {
        success: false,
        error: error.response?.data?.error || error.message || 'Failed to resend verification email'
      };
    }
  }

  /**
   * Request a password reset email. The API always answers with the same
   * neutral message whether or not the account exists.
   */
  async forgotPassword(email: string) {
    try {
      const response = await axios.post(`${API_BASE_URL}/api/user/forgot-password`, { email });
      return { success: true, message: response.data?.message };
    } catch (error: any) {
      const status = error.response?.status;
      if (status === 429) {
        return { success: false, error: 'Too many requests. Please wait a few minutes and try again.' };
      }
      return {
        success: false,
        error: error.response?.data?.error || error.message || 'Failed to request a password reset'
      };
    }
  }

  /**
   * Non-consuming pre-check of a reset token so the page can show an
   * expired/invalid state before the user types a new password.
   */
  async validateResetToken(token: string): Promise<{ valid: boolean; expired: boolean }> {
    try {
      const response = await axios.post(`${API_BASE_URL}/api/user/reset-password/validate`, { token });
      return {
        valid: response.data?.valid === true,
        expired: response.data?.expired === true
      };
    } catch {
      return { valid: false, expired: false };
    }
  }

  /** Consume the reset token and set the new password. No auto-login. */
  async resetPassword(token: string, newPassword: string) {
    try {
      const response = await axios.post(`${API_BASE_URL}/api/user/reset-password`, { token, newPassword });
      return { success: true, message: response.data?.message };
    } catch (error: any) {
      const data = error.response?.data || {};
      return {
        success: false,
        invalidLink: data.code === 'RESET_LINK_INVALID',
        field: data.field,
        error: data.error || error.message || 'Failed to reset password'
      };
    }
  }

  async ssoLogin(provider: 'google' | 'microsoft', inviteHash?: string) {
    try {
      const state = crypto.randomUUID();
      const redirectUri = `${window.location.origin}/auth/callback`;
      
      // Store state for verification
      sessionStorage.setItem('oauth_state', state);
      sessionStorage.setItem('oauth_provider', provider);
      if (inviteHash) {
        sessionStorage.setItem('oauth_invite_hash', inviteHash);
      }
      
      // Build auth URL with invite hash if provided
      let authUrl = `${API_BASE_URL}/api/auth/${provider}?state=${state}&redirect_uri=${encodeURIComponent(redirectUri)}`;
      if (inviteHash) {
        authUrl += `&invite_hash=${encodeURIComponent(inviteHash)}`;
      }
      
      window.location.href = authUrl;
      
      return { success: true, redirecting: true };
    } catch (error: any) {
      return { 
        success: false, 
        error: error.message || 'SSO login failed' 
      };
    }
  }

  async getCurrentUser() {
    // Load from localStorage first
    const token = localStorage.getItem('privacypal_token');
    const userStr = localStorage.getItem('privacypal_user');
    
    if (token && userStr) {
      try {
        this.token = token;
        this.user = JSON.parse(userStr);
        
        // Set default auth header
        axios.defaults.headers.common['x-access-token'] = token;
        
        // Return user - token validation will happen on API calls
        return this.user;
      } catch (e) {
        // Invalid user data in localStorage
        this.logout();
        return null;
      }
    }
    
    return null;
  }

  getToken() {
    return this.token;
  }

  /**
   * Restore session from a token passed from the proxy (e.g. via URL hash).
   * Decodes the JWT payload to get user info and stores the session.
   */
  restoreFromToken(token: string): boolean {
    if (!token || typeof token !== 'string') return false;
    try {
      const parts = token.split('.');
      if (parts.length !== 3) return false;
      const payload = JSON.parse(atob(parts[1].replace(/-/g, '+').replace(/_/g, '/')));
      const userId = payload.user_id || payload.userId;
      const email = payload.email;
      if (!userId || !email) return false;
      this.token = token;
      this.user = { id: userId, email };
      localStorage.setItem('privacypal_token', token);
      localStorage.setItem('privacypal_user', JSON.stringify(this.user));
      (axios.defaults.headers.common as Record<string, string>)['x-access-token'] = token;
      return true;
    } catch {
      return false;
    }
  }

  async logout() {
    this.token = null;
    this.user = null;
    localStorage.removeItem('privacypal_token');
    localStorage.removeItem('privacypal_user');
    delete axios.defaults.headers.common['x-access-token'];
    this.isLoggingOut = false;
  }

  isAuthenticated() {
    return !!this.token && !!this.user;
  }
}

export const authService = new AuthService();

